import { useEffect, useState, useCallback } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { BarChart2, RefreshCcw, Loader2, Wallet, Receipt, TrendingUp, Lock } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";

const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("es-ES").format(n || 0);

/**
 * Live snapshot del día de hoy: ticket count, total, ATV y estado de la
 * caja. Se apoya en endpoints existentes (sales-day/preview + cash/current).
 */
export default function SalesTodayPage() {
  const { currentRestaurant } = useOutletContext();
  const [data, setData] = useState(null);
  const [cash, setCash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!currentRestaurant?.restaurant_id) return;
    const rid = currentRestaurant.restaurant_id;
    try {
      const [preview, current] = await Promise.all([
        api.request(`/restaurants/${rid}/closings/sales-day/preview`),
        api.request(`/restaurants/${rid}/closings/cash/current`).catch(() => null),
      ]);
      setData(preview);
      setCash(current);
    } catch {
      toast.error("No se pudieron cargar las ventas del día");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentRestaurant?.restaurant_id]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000); // 30s polling for live feel
    return () => clearInterval(iv);
  }, [load]);

  const totals = data?.totals || {};
  const totalSales = totals.total_amount || totals.total || 0;
  const ticketCount = totals.ticket_count || totals.tickets_count || totals.tickets || 0;
  const avgTicket = totals.avg_ticket || (ticketCount ? totalSales / ticketCount : 0);
  const cashSession = cash?.session;
  const cashStatus = cashSession?.status || (cashSession ? "open" : "closed");

  return (
    <div className="space-y-6" data-testid="sales-today-page">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center flex-shrink-0">
            <BarChart2 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Ventas del día</h1>
            <p className="text-sm text-muted-foreground">
              Lo que está pasando ahora mismo en tu sala — actualizado cada 30 segundos.
            </p>
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => { setRefreshing(true); load(); }}
          disabled={refreshing}
          data-testid="sales-today-refresh-btn"
        >
          <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  <TrendingUp className="w-3.5 h-3.5" /> Ventas brutas
                </div>
                <p className="text-3xl font-semibold" data-testid="sales-today-total">{fmtEUR(totalSales)}</p>
                <p className="text-xs text-muted-foreground mt-1">Hoy · sin cerrar</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  <Receipt className="w-3.5 h-3.5" /> Tickets
                </div>
                <p className="text-3xl font-semibold" data-testid="sales-today-tickets">{fmtNum(ticketCount)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  ATV {fmtEUR(avgTicket)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  <Wallet className="w-3.5 h-3.5" /> Estado de caja
                </div>
                {cashSession ? (
                  <>
                    <Badge className={cashStatus === "open" ? "bg-emerald-100 text-emerald-800" : "bg-muted text-foreground"}>
                      {cashStatus === "open" ? "Caja abierta" : "Caja cerrada"}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-2">
                      Apertura: {fmtEUR(cashSession.opening_amount || 0)}
                    </p>
                  </>
                ) : (
                  <>
                    <Badge className="bg-amber-100 text-amber-800"><Lock className="w-3 h-3 mr-1" /> Sin abrir</Badge>
                    <p className="text-xs text-muted-foreground mt-2">Abre la caja para empezar el día</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-medium text-sm">¿Vas a cerrar el día?</p>
                <p className="text-xs text-muted-foreground">
                  Realiza el arqueo, el cierre de ventas, inventario y producción desde una única pantalla.
                </p>
              </div>
              <Link to="/app/cash">
                <Button data-testid="sales-today-go-cash">
                  <Wallet className="w-4 h-4 mr-1.5" /> Ir a Caja y Cierres
                </Button>
              </Link>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
